/* ──────────────────────────────────────────────────────────────────────────
   Reading a supplier's .txt of accounts, one account a line.

   Suppliers do not agree on a layout. The usual one is
   username:password:email:email password, but separators differ and some
   lines carry a 2FA key or more. Jay's rule (2026-10-07): the field after an
   email is the email's password, the field after the username is the
   account's password, and a 2FA key may turn up anywhere. Whatever is left is
   kept as "other" and the line is marked unsure, so the upload preview can
   show it to a person rather than guess.

   Two extras are expected and read without a fuss (suppliers' files of
   2026-10-07): the account's own Instagram profile link, which is dropped,
   and a backup email with its password after the main one.

   Pure, with no imports: the upload preview runs it in the browser too.
   ────────────────────────────────────────────────────────────────────────── */

export type ParsedLine = {
  username: string | null;
  password: string | null;
  email: string | null;
  emailPassword: string | null;
  backupEmail: string | null;
  backupEmailPassword: string | null;
  twoFactor: string | null;
  other: string[];
  /** A person should look at it before it is imported. */
  unsure: boolean;
};

export type ParsedFileLine = ParsedLine & { line: number; duplicate: boolean };

const HANDLE = /^[a-z0-9._]{1,30}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BASE32 = /^[A-Z2-7]{16,64}$/;

/** A 2FA key, from a bare key (spaces allowed) or an otpauth:// link. */
export function twoFactorOf(field: string): string | null {
  const value = field.trim();
  if (/^otpauth:\/\//i.test(value)) {
    try {
      const secret = new URL(value).searchParams.get("secret");
      return secret ? secret.replace(/\s+/g, "").toUpperCase() : null;
    } catch {
      return null;
    }
  }
  const compact = value.replace(/\s+/g, "");
  return BASE32.test(compact) ? compact : null;
}

function handleOf(field: string): string | null {
  let value = field.trim();
  const link = /instagram\.com\/([^/?#\s]+)/i.exec(value);
  if (link) value = link[1]!;
  value = value.replace(/^@/, "").toLowerCase();
  return HANDLE.test(value) ? value : null;
}

/**
 * Links hold colons of their own, so they are lifted out before the line is
 * split and put back after.
 */
function fieldsOf(raw: string): string[] {
  const links: string[] = [];
  const masked = raw.replace(/(?:otpauth|https?):\/\/[^\s|;\t]+/gi, (link) => `\u0000${links.push(link) - 1}\u0000`);
  const sep = masked.includes("|") ? "|" : masked.includes(";") ? ";" : masked.includes("\t") ? "\t" : ":";
  return masked
    .split(sep)
    .map((field) => field.replace(/\u0000(\d+)\u0000/g, (_, i) => links[Number(i)]!).trim())
    .filter((field) => field !== "");
}

export function parseLine(raw: string): ParsedLine {
  const fields = fieldsOf(raw);
  const out: ParsedLine = { username: null, password: null, email: null, emailPassword: null, backupEmail: null, backupEmailPassword: null, twoFactor: null, other: [], unsure: false };
  if (!fields.length) return { ...out, unsure: true };

  out.username = handleOf(fields[0]!);
  const rest = fields.slice(1);
  const used = new Set<number>();

  // The account's own profile link says nothing the username does not.
  rest.forEach((field, i) => {
    if (/instagram\.com\//i.test(field) && handleOf(field) === out.username) used.add(i);
  });
  rest.forEach((field, i) => {
    if (used.has(i)) return;
    const key = twoFactorOf(field);
    if (key && !out.twoFactor) {
      out.twoFactor = key;
      used.add(i);
    }
  });
  // The first email is the account's, a second one its backup, each followed by its password.
  rest.forEach((field, i) => {
    if (used.has(i) || !EMAIL.test(field) || out.backupEmail) return;
    const next = i + 1;
    const password = next < rest.length && !used.has(next) && !EMAIL.test(rest[next]!) ? rest[next]! : null;
    if (!out.email) {
      out.email = field;
      out.emailPassword = password;
    } else {
      out.backupEmail = field;
      out.backupEmailPassword = password;
    }
    used.add(i);
    if (password !== null) used.add(next);
  });
  if (rest.length && !used.has(0)) {
    out.password = rest[0]!;
    used.add(0);
  }
  out.other = rest.filter((_, i) => !used.has(i));
  out.unsure = out.username === null || out.password === null || out.other.length > 0;
  return out;
}

export function parseAccountsText(text: string): ParsedFileLine[] {
  const seen = new Set<string>();
  const out: ParsedFileLine[] = [];
  text.split(/\r?\n/).forEach((raw, index) => {
    if (!raw.trim()) return;
    const parsed = parseLine(raw);
    const duplicate = parsed.username !== null && seen.has(parsed.username);
    if (parsed.username) seen.add(parsed.username);
    out.push({ ...parsed, line: index + 1, duplicate });
  });
  return out;
}
